import React from 'react';

// State
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  nextIdea,
  selectIdeaState,
  setShowEmpty,
  refreshIdeas
} from 'src/react/recommendations/store/slices/ideas';
import {
  selectSurveySettings,
  surveyPath
} from 'src/react/recommendations/store/slices/settings';

// Components
import EmptyIdeaList from 'src/react/recommendations/components/EmptyIdeaList/EmptyIdeaList';
import RecommendationIdea from 'src/react/recommendations/components/Idea/Idea';
import Progress from 'src/react/recommendations/components/Idea/Progress';
import Icon from 'src/react/components/icon/Icon';
import IdeaPage from 'src/react/recommendations/components/Idea/IdeaPage';
import { createTask } from 'src/react/recommendations/store/slices/tasks';
import logger from 'src/analytics/logger';
import { setModalOpen } from 'src/react/recommendations/store/slices/modal';
import { IDEA_DETAILS_MODAL } from './RecommendationModalView';
import StartOverExploreMore from 'src/react/recommendations/components/EmptyIdeaList/StartOverExploreMore';
import StartOver from 'src/react/recommendations/components/EmptyIdeaList/StartOver';
import ExploreMore from 'src/react/recommendations/components/EmptyIdeaList/ExploreMore';

const RecommendationIdeas = () => {
  const dispatch = useDispatch();
  const ideaState = useSelector(selectIdeaState);
  const recommendationsSurveyPath = useSelector(surveyPath);
  const surveySettings = useSelector(selectSurveySettings);
  const { ideas, currentIdea, showEmpty, isLastIdea } = ideaState;
  const hasIdeas = ideas.length > 0;
  const showExploreMore = !surveySettings.completed;

  const startOver = () => {
    logger.trackElementClicked({
      elementId: 'Restart Recommendation Loop',
      label: 'Start Over'
    });

    dispatch(refreshIdeas());
  };

  const exploreMore = () => {
    logger.trackElementClicked({
      elementId: 'Launch Recommendations Assessment',
      label: 'Explore More'
    });

    dispatch(refreshIdeas());
  };

  // No additional ideas available to look through
  // & recommendation survey has not yet been completed
  if (!hasIdeas && showExploreMore) {
    return (
      <IdeaPage>
        <ExploreMore
          exploreMoreHandler={exploreMore}
          recommendationsSurveyPath={recommendationsSurveyPath}
        />
      </IdeaPage>
    );
  }

  // No additional ideas available to look through
  if (!hasIdeas) {
    return (
      <IdeaPage>
        <EmptyIdeaList />
      </IdeaPage>
    );
  }

  // Ideas are available to look through & recommendation survey has not yet been completed
  if (showEmpty && showExploreMore) {
    return (
      <IdeaPage>
        <StartOverExploreMore
          restartHandler={startOver}
          exploreMoreHandler={exploreMore}
          recommendationsSurveyPath={recommendationsSurveyPath}
        />
      </IdeaPage>
    );
  }

  // Ideas are available to look through
  if (showEmpty) {
    return (
      <IdeaPage>
        <StartOver restartHandler={startOver} />
      </IdeaPage>
    );
  }

  const idea = ideas[currentIdea];
  const addRecommendation = async () => {
    logger.trackElementClicked({
      elementId: 'Add Recommendation To My List',
      label: 'Add',
      recommendation_id: `RPLUS_${idea.recommendation_id}`
    });
    batch(() => {
      dispatch(createTask(idea));

      if (isLastIdea) {
        dispatch(setShowEmpty());
        return;
      }

      dispatch(nextIdea());
    });
  };

  const notNow = () => {
    logger.trackElementClicked({
      elementId: 'Skip Recommendation',
      label: 'Not Now',
      recommendation_id: `RPLUS_${idea.recommendation_id}`
    });

    if (isLastIdea) {
      return dispatch(setShowEmpty());
    }

    return dispatch(nextIdea());
  };

  const showDetails = () => {
    logger.trackElementClicked({
      elementId: 'Recommendation Learn More',
      label: 'Learn More',
      recommendation_id: `RPLUS_${idea.recommendation_id}`
    });

    dispatch(
      setModalOpen({
        isOpen: true,
        activeModal: IDEA_DETAILS_MODAL,
        recommendationId: idea.recommendation_id
      })
    );
  };

  return (
    <IdeaPage>
      <Icon name="flame" style={{ color: '#FAAB19' }} className="mb-2" />
      <Progress current={currentIdea + 1} total={ideas.length} />
      <RecommendationIdea
        idea={idea}
        onShowDetailsClick={showDetails}
        onAddClick={addRecommendation}
        onSkipClick={notNow}
      />
    </IdeaPage>
  );
};

RecommendationIdeas.propTypes = {};

export default RecommendationIdeas;
